import { FormEvent, useContext } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import {
  DrivingConviction,
  DrivingConvictionError,
  SelectOpt
} from '../CommonTypes';
import SelectInput from '../SelectInput/SelectInput';
import { ButtonContinue } from '../Button/ButtonContinue';
import NumberInput from '../NumberInput/NumberInput';
import SimpleDateInput from '../DateInput/SimpleDateInput';

interface DrivingConvictionsProps {
  convictionTypes: SelectOpt[];
  lengthOfBanTypes: SelectOpt[];
  drivingConvictions: DrivingConviction[];
  errorMessages: DrivingConvictionError[];
  onUpdateDrivingConvictions: (convictions: DrivingConviction[]) => void;
}

const DrivingConvictions = ({
  convictionTypes,
  lengthOfBanTypes,
  drivingConvictions,
  errorMessages,
  onUpdateDrivingConvictions
}: DrivingConvictionsProps) => {
  const { site } = useContext(SiteContext);

  if (drivingConvictions.length === 0)
    drivingConvictions = [
      ...drivingConvictions,
      {
        convictionType: '',
        convictionDate: undefined,
        lengthOfBan: '',
        points: 0
      }
    ];

  const onChangeConvictionType = (value: string, index: number) => {
    const updatedConvictions = drivingConvictions.map((c, i) =>
      i === index ? { ...c, convictionType: value } : { ...c }
    );
    onUpdateDrivingConvictions(updatedConvictions);
  };

  const onChangeLengthOfBan = (value: string, index: number) => {
    const updatedConvictions = drivingConvictions.map((c, i) =>
      i === index ? { ...c, lengthOfBan: value } : { ...c }
    );
    onUpdateDrivingConvictions(updatedConvictions);
  };

  const onChangeConvictionDateValue = (
    date: Date | null,
    index: number
  ): void => {
    const updatedConvictions = drivingConvictions.map((c, i) =>
      i === index
        ? {
            ...c,
            convictionDate: date === null ? undefined : date
          }
        : { ...c }
    );
    onUpdateDrivingConvictions(updatedConvictions);
  };

  const onChangeConvictionPointValue = (
    e: FormEvent<HTMLInputElement>,
    index: number
  ): void => {
    const newPoints = parseInt((e.target as HTMLInputElement).value);

    const updatedConvictions = drivingConvictions.map((c, i) =>
      i === index
        ? {
            ...c,
            points: newPoints === null ? undefined : newPoints
          }
        : { ...c }
    );
    onUpdateDrivingConvictions(updatedConvictions);
  };

  const onClickRemoveConviction = (index: number): void =>
    onUpdateDrivingConvictions(
      drivingConvictions.filter((c, i) => index !== i)
    );

  const onClickAddAnotherConviction = (): void => {
    const newConvictions = [
      ...drivingConvictions,
      {
        convictionType: '',
        convictionDate: undefined,
        lengthOfBan: '',
        points: 0
      }
    ];
    onUpdateDrivingConvictions(newConvictions);
  };

  return (
    <div className='driving-convictions'>
      {drivingConvictions.map((conviction, index) => (
        <div key={`driving-conviction-${index}`}>
          <div className='flex-row'>
            <div className='inline-control'>
              <div className='control-label'>Type of conviction</div>
              <div className='control'>
                <SelectInput
                  name={`type-of-conviction-${index}`}
                  className='input-text w250'
                  value={conviction.convictionType}
                  onChange={(value: string) => {
                    onChangeConvictionType(value, index);
                  }}
                  options={convictionTypes}
                  defaultText='Select...'
                  forceDefault
                  addDefaultOption
                  loadingText='Loading...'
                  error={errorMessages?.at(index)?.convictionTypes ?? ''}
                />
              </div>
            </div>
            <div className='inline-control'>
              <div className='control-label'>Conviction date</div>
              <div className='control'>
                <SimpleDateInput
                  name={`conviction-date-${index}`}
                  value={conviction.convictionDate}
                  error={errorMessages?.at(index)?.convictionDate ?? ''}
                  onDateChange={(date: Date | null) =>
                    onChangeConvictionDateValue(date, index)
                  }
                  maximumDate={new Date()}
                />
              </div>
            </div>
          </div>
          <div className='flex-row'>
            <div className='inline-control'>
              <div className='control-label'>Length of ban</div>
              <div className='control'>
                <SelectInput
                  name={`length-of-ban-${index}`}
                  className='input-text w250'
                  value={conviction.lengthOfBan}
                  onChange={(value: string) => {
                    onChangeLengthOfBan(value, index);
                  }}
                  options={lengthOfBanTypes}
                  defaultText='Select...'
                  forceDefault
                  addDefaultOption
                  loadingText='Loading...'
                  error={errorMessages?.at(index)?.lengthOfBan ?? ''}
                />
              </div>
            </div>
            <div className='inline-control'>
              <div className='control-label'>Points</div>
              <div className='control'>
                <NumberInput
                  value={conviction.points ? conviction.points.toString() : ''}
                  className='number-input'
                  name={`conviction-points-${index}`}
                  culture={site.cultureCode}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeConvictionPointValue(e, index)
                  }
                  decimalPlaces={0}
                  error={errorMessages?.at(index)?.points ?? ''}
                />
              </div>
            </div>
            {index > 0 && (
              <div
                className='inline-control remove-button-wrapper'
                style={{ alignContent: 'end' }}>
                <ButtonContinue
                  name={`remove-claim-line-${index}`}
                  text={'Remove'}
                  onClick={() => onClickRemoveConviction(index)}
                  additionalClassNames='btn-cancel'
                />
              </div>
            )}
          </div>
          {drivingConvictions.length - 1 === index &&
            drivingConvictions.length < 5 && (
              <div className='add-another-button-wrapper'>
                <ButtonContinue
                  name={`add-another-conviction-${index}`}
                  text={'Add another conviction'}
                  onClick={onClickAddAnotherConviction}
                  additionalClassNames='btn-ok'
                />
              </div>
            )}
        </div>
      ))}
    </div>
  );
};

export default DrivingConvictions;
